import { isLocalStorageSupported } from 'javascripts/utils';
import { collaborationServerVersion } from 'javascripts/collaborationServerVersion';
import eio from 'engine.io-client';
import WebSocketWrapper from './WebSocketWrapper';
import EventTarget from 'javascripts/EventTarget';
import secureRandomHexString from 'javascripts/util/secureRandom';

const CLOSED_REASON_INCOMPATIBLE = 4100;

export default class ReliableCollaborationServer extends EventTarget {
  constructor({ baseURL = window.location.origin, collaborationURL } = {}) {
    super();
    this.socket = null;
    this.eiosocket = null;
    this.errorMessageTimeout = null;
    this.reconnectTimeout = null;
    this.reconnectInterval = 200;
    this.maxReconnectInterval = 5000;
    this.reconnectIntervalGrowthFactor = 1.3;
    this.baseURL = baseURL;
    this.collaborationURL =
      collaborationURL || window.Configatron?.live?.collaborationUrl;

    if (window.crypto) {
      this.clientId = secureRandomHexString(32);
    }

    if (
      isLocalStorageSupported() &&
      window.localStorage.getItem('disableWebSockets') === 'true'
    ) {
      this.transports = ['polling'];
    } else {
      this.transports = ['polling', 'websocket'];
    }

    // Refreshing the page will force a disconnect, and we will
    // automatically try to reconnect, even though we're leaving the
    // page. Disable the event listeners on unload to prevent this.
    window.addEventListener('beforeunload', () => this.close());
  }

  get isConnected() {
    return this.eiosocket && this.eiosocket.readyState === 'open';
  }

  connect() {
    this.disconnect();

    const socket = new eio.Socket(this.collaborationURL, {
      path: '/ce',
      transports: this.transports,
      transportOptions: {
        polling: { requestTimeout: 15000 },
      },
    });
    this.eiosocket = socket;

    socket.addEventListener('open', this._onSocketOpen);
    socket.addEventListener('message', this._onSocketMessage);
    socket.addEventListener('close', this._onSocketClose);
    socket.addEventListener('error', this._onSocketClose);

    if (!this.socket) {
      this.socket = new WebSocketWrapper();
    }

    // Engine.io is _close_ to the WebSocket API, but not quite
    // exact. ShareDB depends on its socket implementing the WebSocket
    // API correctly. So we need to return a wrapped socket that
    // proxies that information correctly.
    //
    // As a bonus, this helps us get around Engine.io not allowing
    // reuse of sockets -- we can reuse the wrapper, without reusing
    // the socket.
    this.socket.wrapSocket(socket);
  }

  disconnect() {
    if (!this.eiosocket) return;

    this.eiosocket.removeEventListener('open', this._onSocketOpen);
    this.eiosocket.removeEventListener('message', this._onSocketMessage);
    this.eiosocket.removeEventListener('close', this._onSocketClose);
    this.eiosocket.removeEventListener('error', this._onSocketClose);
    this.eiosocket.close();
    this.eiosocket = null;
  }

  reconnect() {
    this.disconnect();
    this.reconnectInterval = Math.min(
      this.maxReconnectInterval,
      this.reconnectInterval * this.reconnectIntervalGrowthFactor
    );

    clearTimeout(this.reconnectTimer);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.reconnectInterval);
  }

  close() {
    this.disconnect();
    this.notifyConnectionStateChange();
  }

  _onSocketOpen = () => {
    this.sendConfig(this.eiosocket);
    clearTimeout(this.errorMessageTimeout);
    this.errorMessageTimeout = null;
    this.notifyConnectionStateChange();
  };

  _onSocketClose = () => {
    if (!this.errorMessageTimeout) {
      this.errorMessageTimeout = setTimeout(
        () => this.notifyConnectionStateChange(),
        1500
      );
    }

    this.reconnect();
  };

  _onSocketMessage = data => {
    const decodedData = JSON.parse(data);
    if (decodedData.errorMessage) {
      console.error(`Collaboration server error: ${decodedData.errorMessage}`);
      if (decodedData.code === CLOSED_REASON_INCOMPATIBLE) {
        this.close();
      }
    } else {
      this.socket.triggerMessage(data);
    }
  };

  // Send some info to the collaboration server about the current
  // connection.
  sendConfig(socket) {
    socket.send(
      JSON.stringify({
        type: 'init',
        config: {
          baseURL: this.baseURL,
          clientId: this.clientId,
          compatibleVersion: collaborationServerVersion,
        },
      })
    );
  }

  notifyConnectionStateChange() {
    const event = document.createEvent('Event');
    event.initEvent('connectionstatechange', true, true);
    event.isConnected = this.eiosocket?.readyState === 'open';
    this.dispatchEvent(event);
  }
}
